import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Select,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Dismiss20Regular, Document20Regular } from "@fluentui/react-icons";
import { PLICHI, STATI_DOCUMENTO, type Plico, type StatoDocumento } from "@app/schema";
import type { FolderItem } from "@app/sp-data";
import { useData } from "../data/DataProvider";
import { creaVoce, salvaVoce, type VoceChecklist } from "../data/checklist";
import type { Gara, Lotto } from "../data/gare";
import { FilePicker } from "./FilePicker";
import { describeError } from "./ErrorState";

const useStyles = makeStyles({
  surface: { maxWidth: "720px", width: "100%" },
  form: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  three: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalM,
    "& > *": { minWidth: 0 },
    "@media (max-width: 640px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  full: { width: "100%", minWidth: 0 },
  file: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  link: { display: "inline-flex", alignItems: "center", gap: "4px", overflowWrap: "anywhere" },
  meta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

interface FileScelto {
  nome?: string;
  id?: string;
  url?: string;
}

/** Nome leggibile da un link incollato (ultimo segmento del percorso). */
function nomeDaUrl(url: string): string {
  try {
    const u = new URL(url);
    const seg = decodeURIComponent(u.pathname.split("/").filter(Boolean).pop() ?? "");
    return seg && !/\.aspx$/i.test(seg) ? seg : u.hostname;
  } catch {
    return url;
  }
}

/**
 * Modifica (o aggiunta) di un documento della checklist: nome, plico, soggetto,
 * stato, note e collegamento al file nella cartella di gara o del lotto.
 */
export function VoceDialog(props: {
  gara: Gara;
  lotto?: Lotto;
  voce?: VoceChecklist;
  /** Voci della stessa checklist (per l'ordine delle nuove). */
  voci: VoceChecklist[];
  soggetti: string[];
  /** Cartella della gara o del lotto, se già creata. */
  cartella?: FolderItem;
  onClose: (saved: boolean) => void;
}) {
  const s = useStyles();
  const data = useData();
  const v = props.voce?.fields;
  const [titolo, setTitolo] = useState(v?.Title ?? "");
  const [plico, setPlico] = useState<Plico>(v?.Plico ?? "Documentazione Amministrativa");
  const [soggetto, setSoggetto] = useState(v?.Soggetto ?? "");
  const [stato, setStato] = useState<StatoDocumento>(v?.Stato ?? "da fare");
  const [note, setNote] = useState(v?.Note ?? "");
  const [file, setFile] = useState<FileScelto>({ nome: v?.FileNome, id: v?.FileId, url: v?.FileUrl });
  const [scelta, setScelta] = useState<"sfoglia" | "link">();
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  // Soggetti proposti: quelli della gara più quello già indicato (se diverso)
  const soggetti = v?.Soggetto && !props.soggetti.includes(v.Soggetto) ? [...props.soggetti, v.Soggetto] : props.soggetti;

  async function save() {
    if (!titolo.trim()) return setErr("Indica il nome del documento.");
    setBusy(true);
    setErr(undefined);
    try {
      const campi = {
        Title: titolo,
        Plico: plico,
        Soggetto: soggetto,
        Stato: stato,
        Note: note,
        FileNome: file.url ? file.nome : "",
        FileId: file.url ? file.id : "",
        FileUrl: file.url ?? "",
      };
      if (props.voce) await salvaVoce(data, props.voce, campi);
      else await creaVoce(data, props.gara, props.lotto, campi, props.voci);
      props.onClose(true);
    } catch (e) {
      setErr(describeError(e).detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose(false)}>
      <DialogSurface className={s.surface}>
        <DialogBody>
          <DialogTitle>{props.voce ? "Documento" : "Nuovo documento"}</DialogTitle>
          <DialogContent className={s.form}>
            {err && (
              <MessageBar intent="error">
                <MessageBarBody>{err}</MessageBarBody>
              </MessageBar>
            )}
            <Field label="Documento" required>
              <Input value={titolo} onChange={(_, d) => setTitolo(d.value)} />
            </Field>
            <div className={s.three}>
              <Field label="Plico">
                <Select className={s.full} value={plico} onChange={(_, d) => setPlico(d.value as Plico)}>
                  {PLICHI.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Soggetto">
                <Select className={s.full} value={soggetto} onChange={(_, d) => setSoggetto(d.value)}>
                  <option value="">—</option>
                  {soggetti.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Stato">
                <Select className={s.full} value={stato} onChange={(_, d) => setStato(d.value as StatoDocumento)}>
                  {STATI_DOCUMENTO.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Note" hint={`${note.length}/255`}>
              <Input value={note} maxLength={255} onChange={(_, d) => setNote(d.value)} />
            </Field>

            <Field label="File">
              <div className={s.file}>
                {file.url ? (
                  <>
                    <Link className={s.link} href={file.url} target="_blank" rel="noreferrer">
                      <Document20Regular /> {file.nome || nomeDaUrl(file.url)}
                    </Link>
                    <Button size="small" appearance="subtle" icon={<Dismiss20Regular />} onClick={() => setFile({})}>
                      Scollega
                    </Button>
                  </>
                ) : (
                  <span className={s.meta}>Nessun file collegato.</span>
                )}
                <Button size="small" disabled={!props.cartella} onClick={() => setScelta(scelta === "sfoglia" ? undefined : "sfoglia")}>
                  Scegli nella cartella
                </Button>
                <Button size="small" onClick={() => setScelta(scelta === "link" ? undefined : "link")}>
                  Incolla un link
                </Button>
              </div>
            </Field>
            {!props.cartella && scelta !== "link" && (
              <span className={s.meta}>Le cartelle {props.lotto ? "del lotto" : "della gara"} non sono ancora state create: puoi incollare un link.</span>
            )}
            {scelta === "sfoglia" && props.cartella && (
              <FilePicker
                folders={data.repository}
                root={props.cartella}
                selectedId={file.id}
                onPick={(f) => {
                  setFile({ nome: f.name, id: f.id, url: f.webUrl });
                  setScelta(undefined);
                }}
              />
            )}
            {scelta === "link" && (
              <div className={s.file}>
                <Input style={{ flexGrow: 1 }} placeholder="https://…" value={link} onChange={(_, d) => setLink(d.value)} />
                <Button
                  appearance="primary"
                  disabled={!/^https?:\/\/\S+$/i.test(link.trim())}
                  onClick={() => {
                    setFile({ url: link.trim(), nome: nomeDaUrl(link.trim()) });
                    setLink("");
                    setScelta(undefined);
                  }}
                >
                  Collega
                </Button>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => props.onClose(false)} disabled={busy}>
              Annulla
            </Button>
            <Button appearance="primary" onClick={save} disabled={busy}>
              {busy ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
